import React, { useState, useMemo, useEffect } from 'react';
import { Box, TextField, Paper, Typography, Alert, CircularProgress } from '@mui/material';
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import Papa, { ParseResult } from 'papaparse';
import { getMrrMovementCSV } from '../organizations.api';

interface MrrMovementRow {
    week: string;
    mrr_gained: number;
    mrr_lost: number;
}

interface ChartRow {
    week: string;
    mrr_gained: number;
    mrr_lost: number;
    net_mrr: number;
}

/**
 * Normalize a date string to YYYY-MM-DD format.
 * Handles both YYYY-MM-DD and M/D/YY (or M/D/YYYY) formats from Stripe Sigma.
 */
const normalizeWeekDate = (raw: string): string => {
    const str = String(raw).trim();

    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        return str.substring(0, 10);
    }

    // M/D/YY or M/D/YYYY
    const mdyMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (mdyMatch) {
        let year = Number(mdyMatch[3]);
        if (year < 100) {
            year += 2000;
        }
        const month = String(mdyMatch[1]).padStart(2, '0');
        const day = String(mdyMatch[2]).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    return str;
};

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(value);
};

const formatWeekLabel = (week: string | number) => {
    if (typeof week !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(week)) {
        return String(week);
    }
    // Parse YYYY-MM-DD parts directly to avoid timezone issues
    const [y, m, d] = week.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const MrrMovement: React.FC = () => {
    const [rawData, setRawData] = useState<MrrMovementRow[]>([]);
    const [startWeek, setStartWeek] = useState<string>('');
    const [endWeek, setEndWeek] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [lastUpdated, setLastUpdated] = useState<string>('');
    const [hasSetDefaultDates, setHasSetDefaultDates] = useState<boolean>(false);

    // Load CSV data on mount
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setError('');

            try {
                const response = await getMrrMovementCSV();
                const csvText = response.data;

                const lastModified = response.headers['last-modified'];
                if (lastModified) {
                    setLastUpdated(lastModified);
                }

                Papa.parse(csvText, {
                    header: true,
                    dynamicTyping: true,
                    skipEmptyLines: true,
                    complete: (results: ParseResult<MrrMovementRow>) => {
                        try {
                            const parsed = results.data as any[];
                            const validated: MrrMovementRow[] = parsed
                                .filter((row) => row.week)
                                .map((row) => ({
                                    week: normalizeWeekDate(String(row.week)),
                                    mrr_gained: Number(row.mrr_gained) || 0,
                                    mrr_lost: Number(row.mrr_lost) || 0,
                                }));

                            if (validated.length === 0) {
                                setError('No valid data found in MRR movement CSV');
                                return;
                            }

                            setRawData(validated);
                        } catch {
                            setError('Error parsing MRR movement CSV file.');
                        } finally {
                            setIsLoading(false);
                        }
                    },
                    error: () => {
                        setError('Failed to parse MRR movement CSV file');
                        setIsLoading(false);
                    },
                });
            } catch (error: any) {
                console.error('Error loading MRR movement data:', error);

                if (error.response?.status === 404) {
                    setError(
                        error.response?.data?.error ||
                            'MRR movement files not found. The Sigma queries may not have run yet.',
                    );
                } else {
                    setError(
                        error.response?.data?.error ||
                            'Failed to load MRR movement data. Please try again later.',
                    );
                }
                setIsLoading(false);
            }
        };

        void loadData();
    }, []);

    // Default to last 12 weeks
    useEffect(() => {
        if (rawData.length > 0 && !hasSetDefaultDates) {
            const weeks = rawData.map((d) => d.week).sort();
            if (weeks.length > 0) {
                const latestWeek = weeks[weeks.length - 1];
                const startIndex = Math.max(0, weeks.length - 12);
                setStartWeek(weeks[startIndex]);
                setEndWeek(latestWeek);
                setHasSetDefaultDates(true);
            }
        }
    }, [rawData, hasSetDefaultDates]);

    // Filter and compute chart data
    const chartData = useMemo(() => {
        let filtered = [...rawData];

        if (startWeek) {
            filtered = filtered.filter((row) => row.week >= startWeek);
        }
        if (endWeek) {
            filtered = filtered.filter((row) => row.week <= endWeek);
        }

        return filtered
            .sort((a, b) => a.week.localeCompare(b.week))
            .map(
                (row): ChartRow => ({
                    week: row.week,
                    mrr_gained: row.mrr_gained,
                    mrr_lost: -Math.abs(row.mrr_lost), // Negative for display below axis
                    net_mrr: row.mrr_gained - Math.abs(row.mrr_lost),
                }),
            );
    }, [rawData, startWeek, endWeek]);

    // Summary: latest week vs previous week
    const summary = useMemo(() => {
        if (chartData.length < 1) return null;

        const latest = chartData[chartData.length - 1];
        const previous = chartData.length >= 2 ? chartData[chartData.length - 2] : null;

        const wowGained =
            previous && previous.mrr_gained !== 0
                ? ((latest.mrr_gained - previous.mrr_gained) / Math.abs(previous.mrr_gained)) * 100
                : null;

        const wowLost =
            previous && previous.mrr_lost !== 0
                ? ((Math.abs(latest.mrr_lost) - Math.abs(previous.mrr_lost)) /
                      Math.abs(previous.mrr_lost)) *
                  100
                : null;

        const wowNet =
            previous && previous.net_mrr !== 0
                ? ((latest.net_mrr - previous.net_mrr) / Math.abs(previous.net_mrr)) * 100
                : null;

        return {
            gained: latest.mrr_gained,
            lost: Math.abs(latest.mrr_lost),
            net: latest.net_mrr,
            weekLabel: latest.week,
            wowGained,
            wowLost,
            wowNet,
        };
    }, [chartData]);

    const formatWow = (pct: number | null) => {
        if (pct === null) return '';
        const sign = pct >= 0 ? '+' : '';
        return `${sign}${pct.toFixed(1)}% WoW`;
    };

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Weekly MRR movement showing business gained vs lost. Net MRR = Gained - Lost.
            </Typography>

            {lastUpdated && (
                <Typography
                    variant="caption"
                    color="#AAA"
                    sx={{ mb: 3, display: 'block', fontSize: '0.7rem', fontStyle: 'italic' }}
                >
                    Last updated: {lastUpdated}
                </Typography>
            )}

            {isLoading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!isLoading && rawData.length > 0 && (
                <>
                    {/* Summary Cards */}
                    {summary && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 3,
                                mb: 3,
                                flexWrap: 'wrap',
                            }}
                        >
                            {/* MRR Gained */}
                            <Box
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'grey.300',
                                    borderRadius: '4px',
                                    minWidth: '180px',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    MRR Gained
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ color: '#4caf50', fontWeight: 'bold' }}
                                >
                                    {formatCurrency(summary.gained)}
                                </Typography>
                                {summary.wowGained !== null && (
                                    <Typography variant="caption" color="text.secondary">
                                        {formatWow(summary.wowGained)}
                                    </Typography>
                                )}
                            </Box>

                            {/* MRR Lost */}
                            <Box
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'grey.300',
                                    borderRadius: '4px',
                                    minWidth: '180px',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    MRR Lost
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ color: '#f44336', fontWeight: 'bold' }}
                                >
                                    {formatCurrency(summary.lost)}
                                </Typography>
                                {summary.wowLost !== null && (
                                    <Typography variant="caption" color="text.secondary">
                                        {formatWow(summary.wowLost)}
                                    </Typography>
                                )}
                            </Box>

                            {/* Net MRR */}
                            <Box
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'grey.300',
                                    borderRadius: '4px',
                                    minWidth: '180px',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Net MRR Change
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: summary.net >= 0 ? '#4caf50' : '#f44336',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {formatCurrency(summary.net)}
                                </Typography>
                                {summary.wowNet !== null && (
                                    <Typography variant="caption" color="text.secondary">
                                        {formatWow(summary.wowNet)}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Date Range Filter */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <TextField
                            label="Start Week"
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={startWeek}
                            onChange={(e) => setStartWeek(e.target.value)}
                            sx={{ width: '180px' }}
                        />
                        <TextField
                            label="End Week"
                            type="date"
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={endWeek}
                            onChange={(e) => setEndWeek(e.target.value)}
                            sx={{ width: '180px' }}
                        />
                    </Box>

                    {/* Chart */}
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Weekly MRR Movement
                        </Typography>
                        {chartData.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography color="text.secondary">
                                    No data to display. Please adjust your date range.
                                </Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={400}>
                                <ComposedChart
                                    data={chartData}
                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="week" tickFormatter={formatWeekLabel} />
                                    <YAxis
                                        tickFormatter={(value: number) => formatCurrency(value)}
                                    />
                                    <Tooltip
                                        formatter={(value: number, name: string) => [
                                            formatCurrency(Math.abs(value)),
                                            name,
                                        ]}
                                        labelFormatter={formatWeekLabel}
                                    />
                                    <Legend />
                                    <ReferenceLine y={0} stroke="#666" />
                                    <Bar
                                        dataKey="mrr_gained"
                                        name="MRR Gained"
                                        fill="#4caf50"
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Bar
                                        dataKey="mrr_lost"
                                        name="MRR Lost"
                                        fill="#f44336"
                                        radius={[0, 0, 4, 4]}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="net_mrr"
                                        name="Net MRR"
                                        stroke="#2196f3"
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default MrrMovement;
